import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  ReactNode,
} from "react";
import api from "../../api/api"; // Your API instance
import { PaginationInfo } from "../../types/api";

interface VehicleContextType {
  vehicles: any[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchVehicles: (page?: number, perPage?: number) => Promise<void>;
  addVehicle: (vehicle: any) => Promise<void>;
  editVehicle: (updatedVehicle: any) => Promise<void>;
  deleteVehicle: (id: number | string) => Promise<void>;
}

export const VehicleContext = createContext<VehicleContextType | undefined>(
  undefined,
);

interface VehicleProviderProps {
  children: ReactNode;
}

export const useVehicleContext = (): VehicleContextType => {
  const context = useContext(VehicleContext);
  if (!context) {
    throw new Error("useVehicleContext must be used within VehicleProvider");
  }
  return context;
};

const VehicleProvider: React.FC<VehicleProviderProps> = ({ children }) => {
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch vehicles from API with pagination
  const fetchVehicles = useCallback(async (page: number = 1, perPage: number = 15) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await api.get(`/vehicles?${params.toString()}`);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let vehiclesData = [];
      let paginationData = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          vehiclesData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          vehiclesData = responseData.data;
          paginationData = responseData.pagination;
        } else if (Array.isArray(responseData)) {
          vehiclesData = responseData;
        }
      } else if (Array.isArray(responseData?.data)) {
        vehiclesData = responseData.data;
      } else if (Array.isArray(responseData)) {
        vehiclesData = responseData;
      }
      
      setVehicles(vehiclesData);
      setPagination(paginationData);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      setVehicles([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Add vehicle
  const addVehicle = async (vehicle: any): Promise<void> => {
    try {
      const response = await api.post("/vehicles", vehicle);
      const newVehicle = response.data.data || response.data;
      setVehicles((prev) => [...prev, newVehicle]);
    } catch (error) {
      console.error("Error adding vehicle:", error);
      throw error;
    }
  };

  // Edit vehicle
  const editVehicle = async (updatedVehicle: any): Promise<void> => {
    try {
      const response = await api.put(`/vehicles/${updatedVehicle.id}`, updatedVehicle);
      const updatedVehicleData = response.data.data || response.data;
      setVehicles((prev) =>
        prev.map((vehicle) =>
          vehicle.id === updatedVehicle.id ? updatedVehicleData : vehicle
        )
      );
    } catch (error) {
      console.error("Error editing vehicle:", error);
      throw error;
    }
  };

  // Delete vehicle
  const deleteVehicle = async (id: number | string): Promise<void> => {
    try {
      await api.delete(`/vehicles/${id}`);
      setVehicles((prev) => prev.filter((vehicle) => vehicle.id !== id));
    } catch (error) {
      console.error("Error deleting vehicle:", error);
      throw error;
    }
  };

  useEffect(() => {
    fetchVehicles(1, 15);
  }, [fetchVehicles]);

  return (
    <VehicleContext.Provider
      value={{ vehicles, pagination, loading, fetchVehicles, addVehicle, editVehicle, deleteVehicle }}
    >
      {children}
    </VehicleContext.Provider>
  );
};

export default VehicleProvider;
